import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { computationOptions } from "#app/computationOptions";
import { useCompany } from "#modules/company/view/useCompany";
import { useOptimizedMachineTimes } from "#modules/optimizations/view/useOptimizedMachineTimes";
import { useOptimizedMaterialCosts } from "#modules/optimizations/view/useOptimizedMaterialCosts";
import {
  Alert,
  ALERT_TYPE,
  ANALYSIS_2D_FAILED_ALERT_MESSAGE,
  ANALYSIS_3D_FAILED_ALERT_MESSAGE,
  ALERT_VALUES,
  AlertMessage,
  CHILDREN_ALERTS_MESSAGE,
  EMPTY_SURFACE_TREATMENT_ALERT_MESSAGE,
  HIGH_VOLUME_ALERT_MESSAGE,
  LOW_MATERIAL_PRICE_ALERT_MESSAGE,
  LOW_VOLUME_ALERT_MESSAGE,
  MATERIAL_LAYOUT_NOT_FOUND_ALERT_MESSAGE,
  MISSING_ASSEMBLY_STEP_ALERT_MESSAGE,
  MISSING_DIMENSIONS_ALERT_MESSAGE,
  STATIC_ALERT_TO_SKIP,
} from "#modules/quoteRequestLines/domain/alert";
import { QuoteRequestLineDto } from "#modules/quoteRequestLines/infra/fetchQuoteRequestLine.api";
import { useAssemblyAlert } from "#modules/quoteRequestLines/views/useAssemblyAlert";
import {
  checkPartDimensions,
  AssemblyWithChildren,
  computeMaterialPrice,
  formatTimeString,
  getProcessStepName,
  IndustrialProcessSchema,
  JobStatusSchema,
  isSurfaceTreatmentProcessStep,
  OptimizedMachineTimes,
  OptimizedMaterialCosts,
  PartTypeSchema,
  ProcessStep,
  ProcessStepAlertStatic,
  ProcessStepModeSchema,
  ProcessStepAlert,
  TimeDisplayFormatType,
  withOptimizedMachineTimes,
} from "shared";

const staticRuleToMessage = (
  processStep: ProcessStep,
  staticRule: Record<string, number | boolean | string>,
  timeDisplayFormat: TimeDisplayFormatType | undefined,
): {
  message: AlertMessage;
  messageParameters?: Record<string, string | number>;
} | null => {
  if (processStep.mode !== ProcessStepModeSchema.enum.TIME) {
    return null;
  }

  if (
    "maximum" in staticRule &&
    typeof staticRule["maximum"] === "number" &&
    typeof processStep.otherMachineTime === "number" &&
    processStep.otherMachineTime > staticRule["maximum"]
  ) {
    return {
      message: `${ALERT_TYPE.MACHINE_TIME}_${ALERT_VALUES.TOO_HIGH}`,
      messageParameters: { threshold: formatTimeString(staticRule["maximum"], timeDisplayFormat) },
    };
  }

  if (
    "minimum" in staticRule &&
    typeof staticRule["minimum"] === "number" &&
    typeof processStep.otherMachineTime === "number" &&
    processStep.otherMachineTime < staticRule["minimum"]
  ) {
    return {
      message: `${ALERT_TYPE.MACHINE_TIME}_${ALERT_VALUES.TOO_LOW}`,
      messageParameters: { threshold: formatTimeString(staticRule["minimum"], timeDisplayFormat) },
    };
  }

  return null;
};

const shouldShowMaterialCostAlert = ({
  quoteRequestLine,
  hasChildren,
  optimizedMaterialCosts,
}: {
  quoteRequestLine: QuoteRequestLineDto;
  hasChildren: boolean;
  optimizedMaterialCosts: OptimizedMaterialCosts;
}): boolean => {
  if (hasChildren) {
    return false;
  }

  if (quoteRequestLine.part.type === PartTypeSchema.enum.CONSUMABLE) {
    return false;
  }

  const materialPrice = computeMaterialPrice(
    {
      quoteRequestLine,
      optimizedMaterialCosts,
    },
    computationOptions,
  );

  if (materialPrice.partMaterialCost <= 0) {
    return true;
  }

  return false;
};

const shouldShowMaterialLayoutNotFoundAlert = ({
  quoteRequestLine,
  hasChildren,
}: {
  quoteRequestLine: QuoteRequestLineDto;
  hasChildren: boolean;
}): boolean => {
  if (hasChildren) {
    return false;
  }

  if (quoteRequestLine.part.type === PartTypeSchema.enum.CONSUMABLE) {
    return false;
  }

  return !quoteRequestLine.layout?.materialLayout;
};

const shouldShow2DAnalysisFailedAlert = (part: QuoteRequestLineDto["part"]): boolean =>
  part.analyze2DStatus === JobStatusSchema.enum.ERROR &&
  part.analyzeStatus === JobStatusSchema.enum.PROCESSED;

const shouldShow3DAnalysisFailedAlert = (part: QuoteRequestLineDto["part"]): boolean =>
  part.analyzeStatus === JobStatusSchema.enum.ERROR &&
  part.analyze2DStatus === JobStatusSchema.enum.PROCESSED;

export const useAlertChip = (
  quoteRequestLine: QuoteRequestLineDto | undefined,
  assemblyChildren?: AssemblyWithChildren<QuoteRequestLineDto>[],
) => {
  const hasChildren = !!assemblyChildren && assemblyChildren.length > 0;

  const optimizedMachineTimes = useOptimizedMachineTimes(
    quoteRequestLine?.quoteRequestId ?? assemblyChildren?.[0]?.quoteRequestLine.quoteRequestId,
  );
  const optimizedMaterialCosts = useOptimizedMaterialCosts(
    quoteRequestLine?.quoteRequestId ?? assemblyChildren?.[0]?.quoteRequestLine.quoteRequestId,
  );

  const { company } = useCompany();

  const timeDisplayFormat = company?.companySettings.timeDisplayFormat;

  const { t } = useTranslation();

  const { shouldShowAssemblyAlert } = useAssemblyAlert({
    processSteps: quoteRequestLine?.processSteps,
    hasChildren,
  });

  const quoteRequestLineAlerts = getAllAlerts({
    quoteRequestLine,
    hasChildren,
    shouldShowAssemblyAlert,
    timeDisplayFormat,
    optimizedMachineTimes,
    optimizedMaterialCosts,
  });

  const hasChildrenAlerts = useMemo(() => {
    return (assemblyChildren ?? []).some((child) => {
      const childAlerts = getAllAlerts({
        quoteRequestLine: child.quoteRequestLine,
        hasChildren: false,
        shouldShowAssemblyAlert: false,
        timeDisplayFormat,
        optimizedMachineTimes,
        optimizedMaterialCosts,
      });
      return childAlerts.length > 0;
    });
  }, [assemblyChildren, optimizedMachineTimes, optimizedMaterialCosts, timeDisplayFormat]);

  if (hasChildrenAlerts) {
    quoteRequestLineAlerts.unshift({
      processStepId: "children_alerts",
      industrialProcess: null,
      industrialProcessName: null,
      raiseAlert: true,
      severity: "warning",
      message: CHILDREN_ALERTS_MESSAGE,
    });
  }

  const alertCount = quoteRequestLineAlerts.length;
  const hasAlert = alertCount > 0;
  const firstMessage = quoteRequestLineAlerts[0];

  const previewMessage = firstMessage ? t(`alert.${firstMessage.message}_short`) : "";

  const detailedMessages = quoteRequestLineAlerts.map((message) => {
    if (message.processStepId === "material_cost_alert") {
      return t(`alert.${message.message}`, message.messageParameters || undefined);
    }

    return `${message.industrialProcessName ? `${message.industrialProcessName} :` : ""} ${t(
      `alert.${message.message}_short`,
      message.messageParameters || undefined,
    )}`;
  });

  return {
    hasAlert,
    alertCount,
    previewMessage,
    detailedMessages,
    alerts: quoteRequestLineAlerts,
  };
};

const getAllAlerts = ({
  quoteRequestLine,
  hasChildren,
  shouldShowAssemblyAlert,
  timeDisplayFormat,
  optimizedMachineTimes,
  optimizedMaterialCosts,
}: {
  quoteRequestLine: QuoteRequestLineDto | undefined;
  hasChildren: boolean;
  shouldShowAssemblyAlert: boolean;
  timeDisplayFormat: TimeDisplayFormatType | undefined;
  optimizedMachineTimes: OptimizedMachineTimes;
  optimizedMaterialCosts: OptimizedMaterialCosts;
}) => {
  if (!quoteRequestLine) {
    return [];
  }

  const showMaterialCostAlert = shouldShowMaterialCostAlert({
    quoteRequestLine,
    hasChildren,
    optimizedMaterialCosts,
  });

  const showMaterialLayoutNotFoundAlert = shouldShowMaterialLayoutNotFoundAlert({
    quoteRequestLine,
    hasChildren,
  });

  const quoteRequestLineAlerts = quoteRequestLine.processSteps.reduce<Alert[]>(
    (acc, rawProcessStep) => {
      const processStep = withOptimizedMachineTimes(rawProcessStep, optimizedMachineTimes);
      const alertData = processStep.alerts as ProcessStepAlert | null;

      const staticRule = alertData?.conditions?.static_rule;

      if (alertData?.isOverwrittenByFormula?.otherMachineTime) {
        return acc;
      }

      if (
        isSurfaceTreatmentProcessStep(processStep) &&
        !processStep.fixedCost &&
        !processStep.fixedCostPerSeries &&
        !processStep.machineHourlyPrice
      ) {
        acc.push({
          processStepId: processStep.id,
          industrialProcess: processStep.industrialProcess,
          industrialProcessName: getProcessStepName(processStep),
          raiseAlert: true,
          message: EMPTY_SURFACE_TREATMENT_ALERT_MESSAGE,
          severity: "warning",
        });

        return acc;
      }

      if (alertData?.raise_alert && staticRule && "code" in staticRule) {
        const staticAlertRule = staticRule as ProcessStepAlertStatic;
        const alertCode = staticAlertRule.code;

        if (STATIC_ALERT_TO_SKIP.includes(alertCode)) {
          return acc;
        }

        acc.push({
          processStepId: processStep.id,
          industrialProcess: processStep.industrialProcess,
          industrialProcessName: getProcessStepName(processStep),
          raiseAlert: true,
          message: alertCode,
          code: alertCode,
          severity: staticAlertRule.severity,
          conditions: { staticRule: staticAlertRule },
        });
        return acc;
      }

      if (!staticRule) {
        return acc;
      }

      const message = staticRuleToMessage(processStep, staticRule, timeDisplayFormat);

      if (!message) {
        return acc;
      }

      acc.push({
        processStepId: processStep.id,
        industrialProcess: processStep.industrialProcess,
        industrialProcessName: getProcessStepName(processStep),
        raiseAlert: true,
        message: message.message,
        messageParameters: message.messageParameters,
        severity: "warning",
        conditions: {
          staticRule,
        },
      });

      return acc;
    },
    [],
  );

  if (shouldShowAssemblyAlert) {
    quoteRequestLineAlerts.unshift({
      processStepId: "assembly_alert",
      industrialProcess: IndustrialProcessSchema.enum.ASSEMBLING,
      industrialProcessName: getProcessStepName({
        industrialProcess: IndustrialProcessSchema.enum.ASSEMBLING,
        description: null,
      }),
      raiseAlert: true,
      severity: "warning",
      message: MISSING_ASSEMBLY_STEP_ALERT_MESSAGE,
    });
  }

  if (showMaterialCostAlert) {
    quoteRequestLineAlerts.unshift({
      processStepId: "material_cost_alert",
      industrialProcess: null,
      industrialProcessName: null,
      raiseAlert: true,
      severity: "warning",
      message: LOW_MATERIAL_PRICE_ALERT_MESSAGE,
    });
  }

  if (showMaterialLayoutNotFoundAlert) {
    quoteRequestLineAlerts.unshift({
      processStepId: "material_layout_not_found_alert",
      industrialProcess: null,
      industrialProcessName: null,
      raiseAlert: true,
      severity: "error",
      message: MATERIAL_LAYOUT_NOT_FOUND_ALERT_MESSAGE,
    });
  }

  if (shouldShow2DAnalysisFailedAlert(quoteRequestLine.part)) {
    quoteRequestLineAlerts.unshift({
      processStepId: "analysis_2d_failed_alert",
      industrialProcess: null,
      industrialProcessName: null,
      raiseAlert: true,
      severity: "warning",
      message: ANALYSIS_2D_FAILED_ALERT_MESSAGE,
    });
  }

  if (shouldShow3DAnalysisFailedAlert(quoteRequestLine.part)) {
    quoteRequestLineAlerts.unshift({
      processStepId: "analysis_3d_failed_alert",
      industrialProcess: null,
      industrialProcessName: null,
      raiseAlert: true,
      severity: "warning",
      message: ANALYSIS_3D_FAILED_ALERT_MESSAGE,
    });
  }

  const partDimensionCheck = checkPartDimensions(quoteRequestLine.part);

  if (partDimensionCheck.hasWarning) {
    const data = {
      industrialProcess: null,
      industrialProcessName: null,
      raiseAlert: true,
      severity: "warning" as const,
    };

    if (partDimensionCheck.warning === "volume_too_low") {
      quoteRequestLineAlerts.unshift({
        ...data,
        processStepId: "low_volume_alert",
        message: LOW_VOLUME_ALERT_MESSAGE,
      });
    }

    if (partDimensionCheck.warning === "volume_too_high") {
      quoteRequestLineAlerts.unshift({
        ...data,
        processStepId: "high_volume_alert",
        message: HIGH_VOLUME_ALERT_MESSAGE,
      });
    }
  }

  if (partDimensionCheck.hasError) {
    quoteRequestLineAlerts.unshift({
      processStepId: "dimensions_alert",
      industrialProcess: null,
      industrialProcessName: null,
      raiseAlert: true,
      severity: "error",
      message:
        partDimensionCheck.error === "missing_dimension"
          ? MISSING_DIMENSIONS_ALERT_MESSAGE
          : HIGH_VOLUME_ALERT_MESSAGE,
    });
  }

  return quoteRequestLineAlerts;
};
